import { userAtom } from '@/atoms/user.atom';
import { batchStatusMap, formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { Project } from '@/graphql/graphql';
import PageSkeleton from '@/layout/Skeleton';
import { useAlert } from '@/providers/AlertProvider';
import { deleteBatchMutation, getBatch, resetBatchMutation } from '@/queries/attachment.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { RotateCcw, Trash2 } from 'lucide-react';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';
import { toast } from 'sonner';
import AttachmentList from './AttachmentList';
import Header from './Header';
import JobList from './JobList';

interface iindexBatchPage {}

const BatchPage: FunctionComponent<iindexBatchPage> = () => {
  const [shouldPoll, setShouldPoll] = useState(false);
  const user = useAtomValue(userAtom);
  const isActegonUser = user?.email?.endsWith('@actegon.com') ?? false;
  const { href, project, projectLoading } = useOutletContext<{
    href: string;
    project: Project;
    projectLoading: boolean;
  }>();
  const { batchId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openAlert } = useAlert();

  const deleteBatch = useMutation({
    mutationFn: () => execute(deleteBatchMutation, { id: batchId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success('Batch deleted successfully');
      navigate(`${href}/upload`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete batch');
    },
  });

  const resetBatch = useMutation({
    mutationFn: () => execute(resetBatchMutation, { id: batchId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batch', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success('Batch reset successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to reset batch');
    },
  });

  const handleDelete = () => {
    openAlert({
      title: 'Delete Batch',
      content: 'Are you sure you want to delete this batch? This will also delete all associated layers.',
      actions: [{ label: 'Delete', variant: 'destructive', onClick: () => deleteBatch.mutate() }],
    });
  };

  const handleReset = () => {
    openAlert({
      title: 'Reset Batch',
      content:
        'Are you sure you want to reset this batch? This will delete all process jobs and set the batch back to waiting status.',
      actions: [{ label: 'Reset', variant: 'destructive', onClick: () => resetBatch.mutate() }],
    });
  };

  const { data: batchData, isLoading } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => execute(getBatch, { id: batchId! }),
    refetchInterval: shouldPoll ? 500 : false,
  });

  const batch = batchData?.batch;

  useEffect(() => {
    setShouldPoll(batch?.status === 'PROCESSING' || batch?.status === 'UPLOADING');
  }, [batch?.status]);

  if (!batch) return null;
  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? 'Loading...' : project.name, href },
        { label: 'Upload Files', href: `${href}/upload` },
        { label: isLoading ? 'Loading...' : `Batch #${batch?.key?.toUpperCase()}`, href: `${href}/batch/${batchId}` },
      ]}>
      <div className='max-w-screen-md px-4 mx-auto w-full pb-12 pt-12'>
        <Header status={batch?.status} srid={batch.srid || project.srid} attachments={batch?.attachments ?? []} />
        <div className='border rounded-md border-slate-200/70 mt-0 bg-white dark:bg-stone-900 dark:border-stone-800 px-4 py-2.5 items-start gap-4 grid grid-cols-4'>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>Status</span>
            <span className='font-medium text-slate-900 dark:text-white text-left flex items-center gap-1'>
              {batchStatusMap[batch?.status].icon}
              {batchStatusMap[batch?.status].label}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>ID</span>
            <span className='font-medium text-slate-900 dark:text-white text-left'>
              Batch #{batch?.key?.toUpperCase()}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>Uploaded By</span>
            <span className='font-medium text-slate-900 dark:text-white text-left flex items-center gap-1.5'>
              <img
                src={
                  batch?.createdBy?.avatar?.url ||
                  getPlaceholderAvatar(batch?.createdBy?.email || batch?.createdBy?.id || '')
                }
                alt={batch?.createdBy?.name || 'Unknown'}
                className='size-4 rounded-full'
              />
              {batch?.createdBy?.name}
            </span>
          </div>
          <div className='flex items-start gap-0 flex-col'>
            <span className='text-sm text-slate-500 dark:text-stone-500 font-medium text-left'>Uploaded At</span>
            <span className='font-medium text-slate-900 dark:text-white text-left'>
              {formatDateTime(batch?.createdAt ?? new Date())}
            </span>
          </div>
        </div>
        <AttachmentList attachments={batch?.attachments ?? []} batchStatus={batch?.status} />
        <JobList jobs={batch?.jobs ?? []} />
        <div className='mt-6 flex justify-end gap-2'>
          {isActegonUser && (
            <Button variant='outline' onClick={handleReset} disabled={resetBatch.isPending}>
              <RotateCcw className='size-4 mr-2' />
              Reset Batch
            </Button>
          )}
          <Button variant='destructive' onClick={handleDelete} disabled={deleteBatch.isPending}>
            <Trash2 className='size-4 mr-2' />
            Delete Batch
          </Button>
        </div>
      </div>
    </PageSkeleton>
  );
};

export default BatchPage;
